import * as THREE from 'three';
import Experience from './Experience.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

export default class Renderer {
  constructor(_options = {}) {
    this.experience = new Experience();
    this.config = this.experience.config;
    this.debug = this.experience.debug;
    this.stats = this.experience.stats;
    this.time = this.experience.time;
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.camera = this.experience.camera;

    this.usePostprocess = false;

    if (this.debug) {
      this.debugFolder = this.debug.addFolder({
        title: 'renderer'
      });
    }

    this.setInstance();
    this.setPostProcess();
  }

  setInstance() {
    this.clearColor = '#000713';

    if (this.debug) {
      this.debugFolder
        .addInput(this, 'clearColor', {
          view: 'color'
        })
        .on('change', () => {
          this.instance.setClearColor(this.clearColor, 1);
        });
    }

    // Renderer
    this.instance = new THREE.WebGLRenderer({
      alpha: false,
      antialias: true
    });
    this.instance.domElement.style.position = 'absolute';
    this.instance.domElement.style.top = 0;
    this.instance.domElement.style.left = 0;
    this.instance.domElement.style.width = '100%';
    this.instance.domElement.style.height = '100%';

    // this.instance.setClearColor(0x414141, 1)
    this.instance.setClearColor(this.clearColor, 1);
    this.instance.setSize(this.config.width, this.config.height);
    this.instance.setPixelRatio(this.config.pixelRatio);

    // this.instance.physicallyCorrectLights = true
    // this.instance.gammaOutPut = true
    this.instance.outputEncoding = THREE.sRGBEncoding;
    // this.instance.shadowMap.type = THREE.PCFSoftShadowMap
    // this.instance.shadowMap.enabled = true
    // this.instance.shadowMap.autoUpdate = false
    // this.instance.shadowMap.needsUpdate = this.instance.shadowMap.enabled
    // this.instance.toneMapping = THREE.ReinhardToneMapping
    // this.instance.toneMappingExposure = 2.3

    this.context = this.instance.getContext();

    // Add stats panel
    if (this.stats) {
      this.stats.setRenderPanel(this.context);
    }

    // Debug
    if (this.debug) {
      this.debugFolder
        .addInput(this.instance.shadowMap, 'enabled', {
          label: 'shadowMapEnabled'
        })
        .on('change', () => {
          this.scene.traverse((_child) => {
            if (_child instanceof THREE.Mesh) {
              _child.material.needsUpdate = true;
            }
          });

          this.instance.shadowMap.needsUpdate = true;
        });
    }
  }

  setPostProcess() {
    this.postProcess = {};

    /**
     * Render pass
     */
    // Render pass
    this.postProcess.renderPass = new RenderPass(
      this.scene,
      this.camera.instance
    );

    // this.postProcess.outlinePass = new OutlinePass(this.camera.instance);

    // Bloom pass
    // this.postProcess.unrealBloomPass = new UnrealBloomPass(
    //   new THREE.Vector2(this.sizes.width, this.sizes.height),
    //   0.32,
    //   0.52,
    //   0.2
    // );
    // this.postProcess.unrealBloomPass.enabled = true;

    // if (this.debug) {
    //   const debugFolder = this.debugFolder.addFolder({
    //     title: 'UnrealBloomPass'
    //   });

    //   debugFolder.addInput(this.postProcess.unrealBloomPass, 'enabled', {});

    //   debugFolder.addInput(this.postProcess.unrealBloomPass, 'strength', {
    //     min: 0,
    //     max: 3,
    //     step: 0.001
    //   });

    //   debugFolder.addInput(this.postProcess.unrealBloomPass, 'radius', {
    //     min: 0,
    //     max: 1,
    //     step: 0.001
    //   });

    //   debugFolder.addInput(this.postProcess.unrealBloomPass, 'threshold', {
    //     min: 0,
    //     max: 1,
    //     step: 0.001
    //   });
    // }

    /**
     * Effect composer
     */
    // const RenderTargetClass = this.config.pixelRatio >= 2 ? THREE.WebGLRenderTarget : THREE.WebGLMultisampleRenderTarget
    const RenderTargetClass = THREE.WebGLRenderTarget;
    this.renderTarget = new RenderTargetClass(
      this.config.width,
      this.config.height,
      {
        generateMipmaps: false,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBFormat,
        encoding: THREE.sRGBEncoding
      }
    );
    this.postProcess.composer = new EffectComposer(
      this.instance,
      this.renderTarget
    );
    this.postProcess.composer.setSize(this.config.width, this.config.height);
    this.postProcess.composer.setPixelRatio(this.config.pixelRatio);

    this.postProcess.composer.addPass(this.postProcess.renderPass);
    // this.postProcess.composer.addPass(this.postProcess.outlinePass);
    // this.postProcess.composer.addPass(this.postProcess.unrealBloomPass);
  }

  resize() {
    // Instance
    this.instance.setSize(this.config.width, this.config.height);
    this.instance.setPixelRatio(this.config.pixelRatio);

    // Post process
    this.postProcess.composer.setSize(this.config.width, this.config.height);
    this.postProcess.composer.setPixelRatio(this.config.pixelRatio);
  }

  update() {
    if (this.stats) {
      this.stats.beforeRender();
    }

    if (this.usePostprocess) {
      this.postProcess.composer.render();
    } else {
      this.instance.render(this.scene, this.camera.instance);
    }

    if (this.stats) {
      this.stats.afterRender();
    }
  }

  destroy() {
    this.instance.renderLists.dispose();
    this.instance.dispose();
    this.renderTarget.dispose();
    this.postProcess.composer.renderTarget1.dispose();
    this.postProcess.composer.renderTarget2.dispose();
  }
}
